import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { getDocusignTemplates } from 'src/selectors/docusignTemplate'

interface Props {
  insuranceDocusignTemplateId: string | null
}

const InsurancePolicyProductContractCellComponent = ({ insuranceDocusignTemplateId }: Props) => {
  // Read here rather than through the column factory: a factory cannot call `useSelector`, and
  // threading a title map through it would rebuild the columns array when the templates land,
  // resetting the grid's column widths and order.
  const docusignTemplates = useSelector(getDocusignTemplates)

  const template = useMemo(() => {
    if (!insuranceDocusignTemplateId) {
      return null
    }

    const found = docusignTemplates.find((docusignTemplate) => {
      return docusignTemplate.id === insuranceDocusignTemplateId
    })

    return found ?? null
  }, [docusignTemplates, insuranceDocusignTemplateId])

  if (!insuranceDocusignTemplateId) {
    // A dash for a missing optional value is a cosmetic default, not fabricated content.
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        —
      </Typography>
    )
  }

  // A template whose row is gone — or whose list has not loaded — renders the raw id rather
  // than a fabricated title. The admin can still match it against a Retool export or the DB.
  if (!template || !template.title) {
    return (
      <Typography
        variant="body2"
        css={tw`truncate text-shades-500`}
        title={insuranceDocusignTemplateId}
      >
        {insuranceDocusignTemplateId}
      </Typography>
    )
  }

  return (
    <Typography variant="body2" css={tw`truncate`} title={template.title}>
      {template.title}
    </Typography>
  )
}

export const InsurancePolicyProductContractCell = memo(InsurancePolicyProductContractCellComponent)
